import {useTranslation} from "react-i18next";
import React, {useMemo, useState} from "react";
import PlayerNameWithFlag from "../components/players/PlayerNameWithFlag";
import MatchBox from "../components/matches/MatchBox";
import Button from "react-bootstrap/Button";
import {getHallOfFameMatchRatingId} from "../util/HelperUtils";
import {useIsMobile} from "../hooks/useIsMobile";
import { motion, AnimatePresence } from "framer-motion";
import { useHallOfFame } from "../hooks/queries/useStats";

const containerVariants: any = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.15 }
  }
};

const itemVariants: any = {
  hidden: { opacity: 0, y: 25, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.4, ease: [0.34, 1.56, 0.64, 1] }
  }
};

function HallOfFame() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [ ladderId, setLadderId ] = useState(1)
  const { data: records = [] } = useHallOfFame(ladderId);

  const playerRecords = useMemo(() => records.filter(r => r.type === "PLAYER"), [records]);
  const matchRecords = useMemo(() => records.filter(r => r.type === "MATCH"), [records]);

  const setLadder = (newLadderId: number) => setLadderId(newLadderId);

    return (
        <div className="base-container text-center">
          <div className="table-header header">
            <span className="table-title">{t("hallOfFame")}</span>
          </div>
          <div className="header-with-buttons mb-3 mt-3 me-3">
            <Button variant={ladderId === 1 ? "info" : "outline-info"} onClick={() => setLadder(1)} style={{marginLeft: "auto"}} className="table-button" size={isMobile ? "sm" : undefined}>
              {t("advancedLadder")}
            </Button>
            <Button variant={ladderId === 2 ? "warning" : "outline-warning"} onClick={() => setLadder(2)} className="table-button" size={isMobile ? "sm" : undefined}>
              {t("intermediateLadder")}
            </Button>
            <Button variant={ladderId === 3 ? "danger" : "outline-danger"} onClick={() => setLadder(3)} className="table-button" style={{marginRight: "auto"}} size={isMobile ? "sm" : undefined}>
              {t("beginnerLadder")}
            </Button>
          </div>
          <motion.div
            className="m-3"
            style={{fontSize: "xxx-large"}}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            {t("legends")}
          </motion.div>
          <AnimatePresence mode="wait">
            <motion.div
              className="hof-records-grid"
              key={`legends-${ladderId}`}
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {playerRecords.map(record => (
                <motion.div
                  key={"player-record-" + record.category}
                  className="record-box"
                  variants={itemVariants}
                  whileHover={{ scale: 1.03, rotate: -1 }}
                  transition={{ duration: 0.2 }}
                >
                  <h1>{record.value}</h1>
                  <h5>{t(`record_${record.category}`)}</h5>
                  {record.entries.map((entry, idx) => (
                    <PlayerNameWithFlag key={entry.player?.id || idx} player={entry.player} includeElo={false} />
                  ))}
                </motion.div>
              ))}
            </motion.div>
          </AnimatePresence>
          <motion.div
            className="m-3"
            style={{fontSize: "xxx-large"}}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.3 }}
          >
            {t("recordMatches")}
          </motion.div>
          <AnimatePresence mode="wait">
            <motion.div
              className="match-records-grid"
              key={`matches-${ladderId}`}
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {matchRecords.map(record => (
                <motion.div
                  key={"match-record-" + record.category}
                  className="mb-5 match-record"
                  variants={itemVariants}
                >
                  <div className="text-center">
                    <h5>{t(`record_${record.category}`)}</h5>
                  </div>
                  <div className="justify-content-center">
                    {record.entries.map((entry) => entry.match && (
                      <motion.div
                        style={{maxWidth: "720px", margin: "auto"}}
                        key={"match-box-" + entry.match.id}
                        className="shine-on-hover"
                        whileHover={{ scale: 1.01 }}
                        transition={{ duration: 0.2 }}
                      >
                        <MatchBox variant="profile" match={entry.match} hideStatus={true} ratingId={getHallOfFameMatchRatingId(record.category, entry.match)} />
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </AnimatePresence>
        </div>
    );
}

export default HallOfFame
